import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotify } from 'react-admin';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import ResalePricingTabShell from '@src/resalePricing/shared/ResalePricingTabShell';
import { fetchStagingReview } from './stagingApi';
import StagingReviewSummary from './StagingReviewSummary';
import StagingReviewTable from './StagingReviewTable';
import StagingReviewActions from './StagingReviewActions';
import type { StagingReviewResponse } from './stagingApi';

const StagingReviewPage = () => {
  const { adjustmentId } = useParams<{ adjustmentId: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState<StagingReviewResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = Number(adjustmentId);
    if (isNaN(id)) {
      notify('Invalid adjustment ID', { type: 'error' });
      navigate('/resale/overrides');
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchStagingReview(id)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load staging data';
          notify(`Error: ${message}`, { type: 'error' });
          navigate('/resale/overrides');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [adjustmentId, navigate, notify]);

  if (loading) {
    return (
      <ResalePricingTabShell tab="itemAdjustedPrices">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </ResalePricingTabShell>
    );
  }

  if (!data) return null;

  return (
    <ResalePricingTabShell tab="itemAdjustedPrices">
      <Box sx={{ mb: 1.5 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/resale/overrides')}
          sx={{ mb: 1 }}
        >
          Back to Overrides
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Review Staged Adjustment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review the rows below before applying. Errors must be resolved; warnings can be overridden.
        </Typography>
      </Box>

      <StagingReviewSummary summary={data.summary} />
      <StagingReviewTable items={data.items} />
      <StagingReviewActions adjustmentId={data.adjustmentId} summary={data.summary} />
    </ResalePricingTabShell>
  );
};

export default StagingReviewPage;
